import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { resolveUrl } from '../../utils/api';

function MediaPreview({ url, type, name }) {
  if (!url) return null;
  const abs = resolveUrl(url);
  if (type?.startsWith('image/')) {
    return <img src={abs} alt={name} style={S.fileThumb} onError={e => e.target.style.display = 'none'} />;
  }
  if (type?.startsWith('video/')) {
    return <video src={abs} style={S.fileThumb} muted controls />;
  }
  return <a href={abs} target="_blank" rel="noreferrer" style={S.fileLink}>📎 {name}</a>;
}

function EditModal({ msg, onSave, onClose }) {
  const [content, setContent] = useState(msg.content || '');
  const [saving, setSaving] = useState(false);
  const endpoint = msg._type === 'random'
    ? `/admin/random-chat/messages/${msg.id}/`
    : `/admin/messages/${msg.id}/`;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, { content });
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHdr}>
          <span style={{ fontWeight: 700, color: '#e2e8f0' }}>✏️ Edit Message</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <textarea
            style={{ ...S.input, minHeight: 80, resize: 'vertical' }}
            value={content}
            onChange={e => setContent(e.target.value)}
          />
          <button style={S.saveBtn} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editMsg, setEditMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'conversation'
  const [convGroups, setConvGroups] = useState({});

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50, search });
      const res = await api.get(`/admin/messages/?${params}`);
      const msgs = res.data.messages.map(m => ({ ...m, _type: 'private' }));
      setMessages(msgs);
      setTotal(res.data.total);

      // Group by conversation pair for conversation view
      const groups = {};
      msgs.forEach(m => {
        const pair = [m.sender_id, m.receiver_id].sort((a, b) => a - b).join('-');
        if (!groups[pair]) groups[pair] = { users: [m.sender_username, m.receiver_username], msgs: [] };
        groups[pair].msgs.push(m);
      });
      setConvGroups(groups);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const deleteMsg = async (msg) => {
    const endpoint = msg._type === 'random'
      ? `/admin/random-chat/messages/${msg.id}/`
      : `/admin/messages/${msg.id}/`;
    try {
      await api.delete(endpoint);
      setMessages(prev => prev.filter(m => m.id !== msg.id || m._type !== msg._type));
      setConfirmDelete(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>💬 Messages</h1>
          <p style={S.subtitle}>{total} total messages</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...S.viewBtn, ...(viewMode === 'list' ? S.viewBtnActive : {}) }} onClick={() => setViewMode('list')}>List</button>
          <button style={{ ...S.viewBtn, ...(viewMode === 'conversation' ? S.viewBtnActive : {}) }} onClick={() => setViewMode('conversation')}>Conversation</button>
        </div>
      </div>

      <input
        style={S.searchInput}
        placeholder="Search by sender, receiver, or content..."
        value={search}
        onChange={e => { setSearch(e.target.value); setPage(1); }}
      />

      {loading ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : viewMode === 'list' ? (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['ID', 'From → To', 'Content', 'File', 'Status', 'Time', 'Actions'].map(h => (
                    <th key={h} style={S.thCell}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {messages.map(m => (
                  <tr key={m.id} style={S.tr}>
                    <td style={{ ...S.td, color: '#475569', fontSize: 10 }}>#{m.id}</td>
                    <td style={S.td}>
                      <span style={{ color: '#818cf8', fontWeight: 600, fontSize: 11 }}>{m.sender_username}</span>
                      <span style={{ color: '#475569', fontSize: 10 }}> → </span>
                      <span style={{ color: '#94a3b8', fontSize: 11 }}>{m.receiver_username}</span>
                    </td>
                    <td style={{ ...S.td, maxWidth: 220 }}>
                      {m.is_unsent ? (
                        <em style={{ color: '#475569', fontSize: 10 }}>Unsent</em>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 11, wordBreak: 'break-word' }}>
                          {m.content?.slice(0, 80) || '—'}
                          {m.content?.length > 80 && '...'}
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      <MediaPreview url={m.file_url} type={m.file_type} name={m.original_filename} />
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {m.is_read && <span style={{ fontSize: 10, color: '#34d399' }}>✓✓ Read</span>}
                        {m.is_delivered && <span style={{ fontSize: 10, color: '#818cf8' }}>✓ Delivered</span>}
                        {m.is_unsent && <span style={{ fontSize: 10, color: '#f43f5e' }}>Unsent</span>}
                      </div>
                    </td>
                    <td style={{ ...S.td, color: '#475569', fontSize: 10, whiteSpace: 'nowrap' }}>
                      {new Date(m.timestamp).toLocaleString()}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={S.actionBtn} onClick={() => setEditMsg(m)} title="Edit">✏️</button>
                        <button style={S.dangerActionBtn} onClick={() => setConfirmDelete(m)} title="Delete">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={S.pagination}>
              <button style={S.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span style={{ color: '#64748b', fontSize: 11 }}>Page {page} / {totalPages}</span>
              <button style={S.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </>
      ) : (
        /* Conversation view */
        <div style={S.convWrap}>
          {Object.entries(convGroups).map(([pair, group]) => (
            <div key={pair} style={S.convCard}>
              <div style={S.convHdr}>
                <span style={{ color: '#818cf8', fontWeight: 700, fontSize: 12 }}>
                  {group.users[0]} ↔ {group.users[1]}
                </span>
                <span style={{ color: '#475569', fontSize: 10 }}>{group.msgs.length} messages</span>
              </div>
              <div style={S.convMsgs}>
                {group.msgs.map(m => (
                  <div key={m.id} style={{
                    ...S.bubble,
                    alignSelf: m.sender_username === group.users[0] ? 'flex-end' : 'flex-start',
                    background: m.sender_username === group.users[0]
                      ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.05)',
                    borderColor: m.sender_username === group.users[0]
                      ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.07)',
                  }}>
                    <div style={{ fontSize: 10, color: '#818cf8', marginBottom: 4, fontWeight: 600 }}>{m.sender_username}</div>
                    {m.is_unsent ? <em style={{ color: '#475569', fontSize: 10 }}>Message unsent</em> :
                      <span style={{ color: '#e2e8f0', fontSize: 11 }}>{m.content}</span>
                    }
                    <MediaPreview url={m.file_url} type={m.file_type} name={m.original_filename} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: '#475569' }}>{new Date(m.timestamp).toLocaleTimeString()}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={S.miniBtnEdit} onClick={() => setEditMsg(m)}>✏️</button>
                        <button style={S.miniBtnDel} onClick={() => setConfirmDelete(m)}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editMsg && (
        <EditModal msg={editMsg} onSave={fetchMessages} onClose={() => setEditMsg(null)} />
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div style={S.modalOverlay} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...S.modal, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHdr}>
              <span style={{ fontWeight: 700, color: '#f43f5e' }}>⚠️ Delete Message?</span>
              <button style={S.closeBtn} onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ color: '#94a3b8', margin: '0 0 16px', fontSize: 12 }}>
                This will permanently delete the message and any attached files.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={S.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button style={S.dangerBtn} onClick={() => deleteMsg(confirmDelete)}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: { flex: 1, overflowY: 'auto', padding: 24, background: '#07070d', display: 'flex', flexDirection: 'column', gap: 14, animation: 'fadeIn 0.3s ease' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: '#e2e8f0' },
  subtitle: { margin: '4px 0 0', color: '#475569', fontSize: 11 },
  viewBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#64748b', cursor: 'pointer', fontWeight: 600, fontSize: 11 },
  viewBtnActive: { background: 'rgba(99,102,241,0.15)', color: '#818cf8', borderColor: 'rgba(99,102,241,0.3)' },
  searchInput: { padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', width: '100%', maxWidth: 480, boxSizing: 'border-box' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 },
  spinner: { width: 28, height: 28, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  tableWrap: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'auto', flex: 1 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  thCell: { padding: '12px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '10px 16px', verticalAlign: 'middle' },
  actionBtn: { background: 'rgba(99,102,241,0.12)', border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 },
  dangerActionBtn: { background: 'rgba(244,63,94,0.1)', border: 'none', borderRadius: 7, padding: '5px 9px', cursor: 'pointer', fontSize: 11 },
  fileThumb: { width: 56, height: 40, objectFit: 'cover', borderRadius: 6, background: '#1a1a2e' },
  fileLink: { color: '#818cf8', fontSize: 10 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 },
  pageBtn: { padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#94a3b8', cursor: 'pointer', fontSize: 11 },
  convWrap: { display: 'flex', flexDirection: 'column', gap: 16 },
  convCard: { background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden' },
  convHdr: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' },
  convMsgs: { padding: 12, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' },
  bubble: { padding: '8px 12px', borderRadius: 12, border: '1px solid', maxWidth: '80%', display: 'flex', flexDirection: 'column' },
  miniBtnEdit: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: '2px 4px', opacity: 0.7 },
  miniBtnDel: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, padding: '2px 4px', opacity: 0.7 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: '#111118', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
  modalHdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18 },
  input: { padding: '9px 13px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', fontSize: 12, outline: 'none', fontFamily: 'inherit' },
  saveBtn: { padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  dangerBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(244,63,94,0.25)', background: 'rgba(244,63,94,0.12)', color: '#f43f5e', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  cancelBtn: { flex: 1, padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
};
